const BRC77_VERSION = [0x42, 0x42, 0x33, 0x01]
const BRC77_PROTOCOL_ID: [2, 'message signing'] = [2, 'message signing']
const WALLET_REQUEST_TIMEOUT_MS = 90_000

interface Brc100Wallet {
    waitForAuthentication(args: Record<string, never>): Promise<unknown>
    getPublicKey(args: { identityKey: true }): Promise<{ publicKey: string }>
    createSignature?(args: {
        protocolID: [2, 'message signing']
        keyID: string
        counterparty: 'anyone'
        data: number[]
    }): Promise<{ signature: number[] | Uint8Array }>
}

interface LegacyYoursWallet {
    connect(): Promise<string>
    signMessage(args: { message: string }): Promise<{ sig?: string } | undefined>
}

declare global {
    interface Window {
        CWI?: Brc100Wallet
        panda?: LegacyYoursWallet
    }
}

export interface YoursLoginProof {
    pubkey: string
    sig: string
    msg: string
    signature_scheme: 'brc77' | 'bsm'
}

type LoginMessageInput = string | ((identityKey: string) => string | Promise<string>)

async function resolveLoginMessage(
    message: LoginMessageInput,
    identityKey: string,
): Promise<string> {
    return typeof message === 'function' ? message(identityKey) : message
}

function withWalletTimeout<T>(promise: Promise<T>, operation: string): Promise<T> {
    let timeout: ReturnType<typeof setTimeout>

    return new Promise((resolve, reject) => {
        timeout = setTimeout(() => {
            reject(
                new Error(
                    `Yours Wallet did not finish ${operation}. Please approve the wallet request and try again.`,
                ),
            )
        }, WALLET_REQUEST_TIMEOUT_MS)

        promise.then(resolve, reject).finally(() => clearTimeout(timeout))
    })
}

function requireIdentityKey(publicKey: string | undefined): string {
    if (!publicKey?.match(/^(02|03)[0-9a-f]{64}$/i)) {
        throw new Error('Yours Wallet returned an invalid identity key.')
    }

    return publicKey
}

function requireLegacySignature(signedMessage: unknown): string {
    if (
        typeof signedMessage === 'object' &&
        signedMessage !== null &&
        'sig' in signedMessage &&
        typeof signedMessage.sig === 'string' &&
        signedMessage.sig.length > 0
    ) {
        return signedMessage.sig
    }

    throw new Error('Yours Wallet did not sign the login request.')
}

function bytesToBase64(bytes: number[]): string {
    const binary = bytes.map(byte => String.fromCharCode(byte)).join('')
    return btoa(binary)
}

function hexToBytes(hex: string): number[] {
    return hex.match(/.{2}/g)?.map(byte => Number.parseInt(byte, 16)) ?? []
}

async function connectBrc100Wallet(wallet: Brc100Wallet): Promise<string> {
    await withWalletTimeout(wallet.waitForAuthentication({}), 'authentication')
    const { publicKey } = await withWalletTimeout(
        wallet.getPublicKey({ identityKey: true }),
        'identity lookup',
    )
    return requireIdentityKey(publicKey)
}

function getBrc100Wallet(): Brc100Wallet | null {
    const wallet = window.CWI
    if (wallet?.waitForAuthentication && wallet?.getPublicKey) {
        return wallet
    }

    return null
}

function unavailableWalletError(): Error {
    return new Error(
        'Yours Wallet is not available. Please install or update to a BRC-100-compatible version and try again.',
    )
}

export async function getYoursIdentityKey(): Promise<string> {
    const brc100Wallet = getBrc100Wallet()
    if (brc100Wallet) return connectBrc100Wallet(brc100Wallet)

    if (window.panda?.connect) {
        return requireIdentityKey(await withWalletTimeout(window.panda.connect(), 'connection'))
    }

    throw unavailableWalletError()
}

export async function createYoursLoginProof(message: LoginMessageInput): Promise<YoursLoginProof> {
    const brc100Wallet = getBrc100Wallet()
    if (brc100Wallet) {
        if (!brc100Wallet.createSignature) {
            throw new Error('Yours Wallet does not support BRC-77 login signatures.')
        }

        const identityKey = await connectBrc100Wallet(brc100Wallet)
        const loginMessage = await resolveLoginMessage(message, identityKey)
        const keyID = crypto.getRandomValues(new Uint8Array(32))
        const messageBytes = Array.from(new TextEncoder().encode(loginMessage))
        const { signature } = await withWalletTimeout(
            brc100Wallet.createSignature({
                protocolID: BRC77_PROTOCOL_ID,
                keyID: bytesToBase64(Array.from(keyID)),
                counterparty: 'anyone',
                data: messageBytes,
            }),
            'message signing',
        )
        const envelope = [
            ...BRC77_VERSION,
            ...hexToBytes(identityKey),
            0,
            ...keyID,
            ...Array.from(signature),
        ]

        return {
            pubkey: identityKey,
            sig: bytesToBase64(envelope),
            msg: loginMessage,
            signature_scheme: 'brc77',
        }
    }

    const legacyWallet = window.panda
    if (legacyWallet?.connect && legacyWallet?.signMessage) {
        const pubkey = requireIdentityKey(
            await withWalletTimeout(legacyWallet.connect(), 'connection'),
        )
        const loginMessage = await resolveLoginMessage(message, pubkey)
        const signedMessage = await withWalletTimeout(
            legacyWallet.signMessage({ message: loginMessage }),
            'message signing',
        )
        const legacySignature = requireLegacySignature(signedMessage)

        return {
            pubkey,
            sig: legacySignature,
            msg: loginMessage,
            signature_scheme: 'bsm',
        }
    }

    throw unavailableWalletError()
}
